import { create } from 'zustand';
import { api } from '@/lib/api';
import { getAuthToken, setAuthToken } from '@/lib/authToken';
import type { ClientePerfil, PedidoCliente } from '@/types';

interface AuthState {
  token: string | null;
  cliente: ClientePerfil | null;
  pedidos: PedidoCliente[];
  loading: boolean;
  login: (usuario: string, clave: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  setSession: (token: string, cliente: ClientePerfil) => void;
  isLoggedIn: () => boolean;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  token: getAuthToken(),
  cliente: null,
  pedidos: [],
  loading: false,

  isLoggedIn: () => !!get().token,

  setSession: (token, cliente) => {
    setAuthToken(token);
    set({ token, cliente, pedidos: [] });
  },

  login: async (usuario, clave) => {
    set({ loading: true });
    try {
      const data = await api.authLogin(usuario, clave);
      setAuthToken(data.token);
      set({ token: data.token, cliente: data.cliente, pedidos: [] });
      await get().refresh();
    } finally {
      set({ loading: false });
    }
  },

  logout: async () => {
    const token = get().token;
    if (token) {
      try {
        await api.authLogout(token);
      } catch {
        /* ignore */
      }
    }
    setAuthToken(null);
    set({ token: null, cliente: null, pedidos: [] });
  },

  refresh: async () => {
    const token = get().token ?? getAuthToken();
    if (!token) return;
    try {
      const data = await api.authMe(token);
      set({ token, cliente: data.cliente, pedidos: data.pedidos });
    } catch {
      if (!get().cliente) {
        setAuthToken(null);
        set({ token: null, cliente: null, pedidos: [] });
      }
    }
  },
}));
